// Landing page for a seat invitation link: /seat-invite/:token
// Works for both a signed-out invitee (create a password) and someone
// already signed in with the invited email.

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Store, ShieldCheck, AlertTriangle, Check } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import { auth, functions } from '../../config/firebase';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { SEAT_ROLE_LABELS, type SeatRole } from '../../types/business';

interface InvitePreview {
  businessId: string;
  businessName: string;
  email: string;
  displayName: string;
  seatRole: SeatRole;
}

export function SeatInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('This invitation link is missing its code.');
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const getInvite = httpsCallable(functions, 'getSeatInvite');
        const result = await getInvite({ token });
        const data = result.data as InvitePreview;
        setInvite(data);
        setDisplayName(data.displayName || '');
      } catch (err: any) {
        setError(err?.message || 'This invitation is no longer valid.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invite || !token) return;

    setSubmitting(true);
    setError(null);

    try {
      // The seat must be signed in as the invited email before the callable
      // will bind the claims, so sign in or create the account first.
      if (!auth.currentUser || auth.currentUser.email !== invite.email) {
        try {
          await signInWithEmailAndPassword(auth, invite.email, password);
        } catch (signInErr: any) {
          if (
            signInErr?.code === 'auth/user-not-found' ||
            signInErr?.code === 'auth/invalid-credential'
          ) {
            const created = await createUserWithEmailAndPassword(auth, invite.email, password);
            if (displayName.trim()) {
              await updateProfile(created.user, { displayName: displayName.trim() });
            }
          } else {
            throw signInErr;
          }
        }
      }

      const acceptInvite = httpsCallable(functions, 'acceptSeatInvite');
      await acceptInvite({ token, displayName: displayName.trim() || undefined });

      // Claims changed server-side; refresh the token before routing in.
      await auth.currentUser?.getIdToken(true);

      setAccepted(true);
      setTimeout(() => navigate('/business/dashboard', { replace: true }), 1200);
    } catch (err: any) {
      setError(
        err?.code === 'auth/wrong-password'
          ? 'That password does not match the existing account for this email.'
            : err?.code === 'auth/weak-password'
              ? 'Choose a password with at least 8 characters.'
            : err?.message || 'We could not complete your setup. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-7 w-7 text-primary animate-spin" />
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-3" />
            <h1 className="text-lg font-serif text-heading mb-1">Invitation unavailable</h1>
            <p className="text-sm text-body mb-5">{error}</p>
            <Button variant="outline" onClick={() => navigate('/')}>
              Go to FASH
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
              <Check className="h-6 w-6 text-green-600" />
            </div>
            <h1 className="text-lg font-serif text-heading mb-1">You're in</h1>
            <p className="text-sm text-body">Taking you to {invite?.businessName}…</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-10">
      <Card className="w-full max-w-md">
        <CardContent className="p-6 space-y-5">
          <div className="text-center">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
              <Store className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-xl font-serif text-heading">Join {invite?.businessName}</h1>
            <p className="text-sm text-body mt-1">
              You've been invited as{' '}
              <span className="font-medium text-heading">
                {invite ? SEAT_ROLE_LABELS[invite.seatRole]?.label : ''}
              </span>
            </p>
          </div>

          <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 flex items-start gap-2.5">
            <ShieldCheck className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
            <p className="text-xs text-body leading-relaxed">
              Your seat lets you work orders, keep hours current, and see the sales you handle.
              It does not include the store's tax, banking, payout, or revenue information.
            </p>
          </div>

          <form onSubmit={handleAccept} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-heading">Email</label>
              <Input type="email" value={invite?.email || ''} disabled readOnly />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-heading">Your name</label>
              <Input
                type="text"
                placeholder="Jordan Ellis"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-heading">
                Password <span className="text-red-500">*</span>
              </label>
              <Input
                type="password"
                required
                minLength={8}
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="text-xs text-body">
                Already have a FASH account with this email? Enter that password.
              </p>
            </div>

            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Setting up your seat
                </>
              ) : (
                'Accept invitation'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default SeatInvitePage;
